import axios from 'axios';
import { AUTH_CONFIG, API_BASE_URL, DEBUG_MODE } from '../config/apiConfig';
import { apiConstants } from '../constants/api.constant';

/**
 * Centralized Authentication Service
 * Handles all authentication operations including token management,
 * session handling, and user authentication state
 */
class AuthService {
    constructor() {
        this.tokenRefreshPromise = null;
        this.refreshAttempts = 0;
        this.maxRefreshAttempts = AUTH_CONFIG.MAX_REFRESH_ATTEMPTS;
        this.isRefreshing = false;
        
        // Initialize axios interceptors
        this.setupInterceptors();
        
        // Check for existing session on initialization
        this.initializeSession();
    }

    /**
     * Initialize session from stored tokens
     */
    initializeSession() {
        const token = this.getAccessToken();
        const refreshToken = this.getRefreshToken();
        
        if (token && refreshToken) {
            this.log('Session initialized from stored tokens');
            // Optionally validate token here
            this.scheduleTokenRefresh();
        } else {
            this.log('No existing session found');
        }
    }

    /**
     * Setup axios interceptors for automatic token handling
     */
    setupInterceptors() {
        // Request interceptor to add token to headers
        axios.interceptors.request.use(
            (config) => {
                const token = this.getAccessToken();
                if (token && !config.headers.Authorization) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle token refresh
        axios.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                const originalRequest = error.config;
                
                // Skip token refresh for login endpoints
                const isLoginRequest = originalRequest.url && (
                    originalRequest.url.includes('/auth/login') ||
                    originalRequest.url.includes('/auth/otp') ||
                    originalRequest.url.includes('/auth/mfa') ||
                    originalRequest.url.includes('/login') ||
                    originalRequest.url.includes(apiConstants.ADMIN_LOGIN)
                );
                
                if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
                    originalRequest._retry = true;
                    
                    try {
                        await this.refreshToken();
                        // Retry the original request with new token
                        const newToken = this.getAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        this.log('Token refresh failed, logging out user');
                        this.logout();
                        return Promise.reject(refreshError);
                    }
                }
                
                return Promise.reject(error);
            }
        );
    }

    /**
     * Authenticate user with credentials
     * @param {string} username - Username or email
     * @param {string} password - Password
     * @returns {Promise<Object>} Authentication response
     */
    async login(username, password) {
        try {
            this.log('Attempting login', { username });
            
            // Create FormData for FastAPI OAuth2PasswordRequestForm
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);
            
            const response = await axios.post(
                `${API_BASE_URL}${apiConstants.ADMIN_LOGIN}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'x-suppress-global-toast': 'true', // Suppress global error toasts for login
                    }
                }
            );
            
            if (response.data && response.data.access_token) {
                const authData = {
                    access_token: response.data.access_token,
                    refresh_token: response.data.refresh_token,
                    token_type: response.data.token_type,
                    expires_in: response.data.expires_in,
                    user_info: response.data.user_info,
                    session_info: response.data.session_info
                };
                
                // Store tokens and user info
                this.setTokens(authData.access_token, authData.refresh_token);
                this.setUserInfo(authData.user_info);
                
                // Schedule token refresh
                this.scheduleTokenRefresh(authData.expires_in);
                
                this.log('Login successful', { userId: authData.user_info.id });
                
                return {
                    success: true,
                    message: 'Login successful',
                    data: authData
                };
            } else {
                throw new Error('Invalid response format');
            }
        } catch (error) {
            this.log('Login failed', { error: error.message });
            
            // Extract error message with priority on 'error' field from API response
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.detail || 
                               error.response?.data?.message || 
                               error.message || 
                               'Login failed';
            
            return {
                success: false,
                message: errorMessage,
                status_code: error.response?.status,
                request_id: error.response?.data?.request_id,
                error: error.response?.data
            };
        }
    }

    /**
     * Refresh access token using refresh token
     * @returns {Promise<string>} New access token
     */
    async refreshToken() {
        if (this.isRefreshing) {
            // If already refreshing, wait for the existing promise
            return this.tokenRefreshPromise;
        }
        
        this.isRefreshing = true;
        this.refreshAttempts++;
        
        try {
            const refreshToken = this.getRefreshToken();
            
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }
            
            if (this.refreshAttempts > this.maxRefreshAttempts) {
                throw new Error('Max refresh attempts exceeded');
            }
            
            this.log('Refreshing token', { attempt: this.refreshAttempts });
            
            this.tokenRefreshPromise = axios.post(
                `${API_BASE_URL}${apiConstants.REFRESH_TOKEN}`,
                { refresh_token: refreshToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const response = await this.tokenRefreshPromise;
            
            if (response.data && response.data.access_token) {
                const newAccessToken = response.data.access_token;
                const newRefreshToken = response.data.refresh_token;
                
                // Update stored tokens
                this.setTokens(newAccessToken, newRefreshToken);
                
                // Schedule next refresh
                this.scheduleTokenRefresh(response.data.expires_in);
                
                // Reset refresh attempts
                this.refreshAttempts = 0;
                
                this.log('Token refreshed successfully');
                
                return newAccessToken;
            } else {
                throw new Error('Invalid refresh response');
            }
        } catch (error) {
            this.log('Token refresh failed', { error: error.message });
            
            if (this.refreshAttempts >= this.maxRefreshAttempts) {
                this.logout();
            }
            
            throw error;
        } finally {
            this.isRefreshing = false;
            this.tokenRefreshPromise = null;
        }
    }

    /**
     * Logout user and clear session
     */
    async logout() {
        try {
            const token = this.getAccessToken();
            
            if (token) {
                // Call logout endpoint
                await axios.post(
                    `${API_BASE_URL}${apiConstants.LOGOUT}`,
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );
            }
        } catch (error) {
            this.log('Logout API call failed', { error: error.message });
        } finally {
            // Clear local storage regardless of API call success
            this.clearSession();
            this.log('User logged out');
        }
    }

    /**
     * Clear all session data
     */
    clearSession() {
        localStorage.removeItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
        localStorage.removeItem(AUTH_CONFIG.REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem('userInfo');
        
        // Clear any scheduled token refresh
        if (this.tokenRefreshTimeout) {
            clearTimeout(this.tokenRefreshTimeout);
            this.tokenRefreshTimeout = null;
        }
        
        this.refreshAttempts = 0;
        this.isRefreshing = false;
        this.tokenRefreshPromise = null;
    }

    /**
     * Get current access token
     * @returns {string|null} Access token
     */
    getAccessToken() {
        try {
            const authData = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
            if (authData) {
                const parsed = JSON.parse(authData);
                return parsed.authToken || parsed.access_token;
            }
        } catch (error) {
            this.log('Error getting access token', { error: error.message });
        }
        return null;
    }

    /**
     * Get current refresh token
     * @returns {string|null} Refresh token
     */
    getRefreshToken() {
        try {
            const authData = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
            if (authData) {
                const parsed = JSON.parse(authData);
                return parsed.refreshToken || parsed.refresh_token;
            }
        } catch (error) {
            this.log('Error getting refresh token', { error: error.message });
        }
        return null;
    }

    /**
     * Get current user info
     * @returns {Object|null} User information
     */
    getUserInfo() {
        try {
            const authData = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
            if (authData) {
                const parsed = JSON.parse(authData);
                return parsed;
            }
        } catch (error) {
            this.log('Error getting user info', { error: error.message });
        }
        return null;
    }

    /**
     * Set tokens in storage
     * @param {string} accessToken - Access token
     * @param {string} refreshToken - Refresh token
     */
    setTokens(accessToken, refreshToken) {
        try {
            const existingData = this.getUserInfo() || {};
            const updatedData = {
                ...existingData,
                authToken: accessToken,
                refreshToken: refreshToken,
                access_token: accessToken,
                refresh_token: refreshToken,
                lastTokenUpdate: new Date().toISOString()
            };
            
            localStorage.setItem(AUTH_CONFIG.TOKEN_STORAGE_KEY, JSON.stringify(updatedData));
            this.log('Tokens updated in storage');
        } catch (error) {
            this.log('Error setting tokens', { error: error.message });
        }
    }

    /**
     * Set user info in storage
     * @param {Object} userInfo - User information
     */
    setUserInfo(userInfo) {
        try {
            const existingData = this.getUserInfo() || {};
            const updatedData = {
                ...existingData,
                ...userInfo,
                lastUserInfoUpdate: new Date().toISOString()
            };
            
            localStorage.setItem(AUTH_CONFIG.TOKEN_STORAGE_KEY, JSON.stringify(updatedData));
            this.log('User info updated in storage');
        } catch (error) {
            this.log('Error setting user info', { error: error.message });
        }
    }

    /**
     * Check if user is authenticated
     * @returns {boolean} Authentication status
     */
    isAuthenticated() {
        const token = this.getAccessToken();
        const userInfo = this.getUserInfo();
        return !!(token && userInfo);
    }

    /**
     * Schedule automatic token refresh
     * @param {number} expiresIn - Token expiry time in seconds
     */
    scheduleTokenRefresh(expiresIn) {
        if (this.tokenRefreshTimeout) {
            clearTimeout(this.tokenRefreshTimeout);
        }
        
        // Schedule refresh 5 minutes before expiry or at 75% of token lifetime
        const refreshTime = expiresIn ? 
            Math.max(expiresIn * 1000 * 0.75, AUTH_CONFIG.TOKEN_REFRESH_THRESHOLD) :
            AUTH_CONFIG.TOKEN_REFRESH_THRESHOLD;
        
        this.tokenRefreshTimeout = setTimeout(async () => {
            try {
                await this.refreshToken();
            } catch (error) {
                this.log('Scheduled token refresh failed', { error: error.message });
            }
        }, refreshTime);
        
        this.log('Token refresh scheduled', { refreshInMs: refreshTime });
    }

    /**
     * Validate current token
     * @returns {Promise<boolean>} Token validity
     */
    async validateToken() {
        try {
            // 🚀 PERFORMANCE OPTIMIZATION: Add caching and deduplication for token validation
            const token = this.getAccessToken();
            if (!token) {
                return false;
            }
            
            // Check if validation is already in progress
            if (this._validationPromise) {
                this.log('Token validation already in progress, reusing promise');
                return await this._validationPromise;
            }
            
            // Check cache (token validation is valid for 1 minute)
            const cacheKey = `token_validation_${token}`;
            const cached = this._validationCache?.get(cacheKey);
            if (cached && Date.now() - cached.timestamp < 60000) {
                this.log('Using cached token validation result');
                return cached.result;
            }
            
            // Create validation promise
            this._validationPromise = axios.get(
                `${API_BASE_URL}${apiConstants.VALIDATE_TOKEN}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            ).then(response => {
                const result = response.status === 200;
                
                // Cache the result
                if (!this._validationCache) {
                    this._validationCache = new Map();
                }
                this._validationCache.set(cacheKey, {
                    result,
                    timestamp: Date.now()
                });
                
                return result;
            }).finally(() => {
                // Clear the promise after completion
                this._validationPromise = null;
            });
            
            return await this._validationPromise;
        } catch (error) {
            this.log('Token validation failed', { error: error.message });
            return false;
        }
    }

    /**
     * Get current session info
     * @returns {Promise<Object>} Session information
     */
    async getSessionInfo() {
        try {
            const response = await axios.get(
                `${API_BASE_URL}${apiConstants.GET_SESSION}`
            );
            
            return response.data;
        } catch (error) {
            this.log('Failed to get session info', { error: error.message });
            throw error;
        }
    }

    /**
     * Change user password
     * @param {string} currentPassword - Current password
     * @param {string} newPassword - New password
     * @param {string} confirmPassword - Confirm password
     * @returns {Promise<Object>} Change password response
     */
    async changePassword(currentPassword, newPassword, confirmPassword, userId = null) {
        try {
            let response;

            // Use different endpoint for admin reset vs self password change
            if (userId && !currentPassword) {
                // Admin reset - use users endpoint
                response = await axios.put(
                    `${API_BASE_URL}/users/${userId}/password`,
                    {
                        new_password: newPassword,
                        confirm_password: confirmPassword
                    }
                );
            } else {
                // Regular password change - use auth endpoint
                response = await axios.post(
                    `${API_BASE_URL}${apiConstants.CHANGE_PASSWORD}`,
                    {
                        current_password: currentPassword,
                        new_password: newPassword,
                        confirm_password: confirmPassword
                    }
                );
            }

            return {
                success: true,
                message: 'Password changed successfully',
                data: response.data
            };
        } catch (error) {
            this.log('Password change error:', error);

            let errorMessage = 'Password change failed';

            // Handle different error formats
            if (error.response?.data?.detail) {
                if (Array.isArray(error.response.data.detail)) {
                    // Handle validation error array
                    errorMessage = error.response.data.detail.map(err => {
                        if (typeof err === 'object') {
                            return err.msg || err.message || 'Validation error';
                        }
                        return String(err);
                    }).join(', ');
                } else if (typeof error.response.data.detail === 'string') {
                    errorMessage = error.response.data.detail;
                } else {
                    errorMessage = 'Validation failed';
                }
            } else if (error.response?.data?.message) {
                errorMessage = String(error.response.data.message);
            }

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Request password reset
     * @param {string} email - User email
     * @returns {Promise<Object>} Reset request response
     */
    async forgotPassword(email) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}${apiConstants.FORGOT_PASSWORD}`,
                { email }
            );
            
            return {
                success: true,
                message: 'Password reset email sent',
                data: response.data
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Password reset request failed';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Reset password with token
     * @param {string} token - Reset token
     * @param {string} newPassword - New password
     * @returns {Promise<Object>} Reset response
     */
    async resetPassword(token, newPassword) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}${apiConstants.RESET_PASSWORD}`,
                {
                    token,
                    new_password: newPassword
                }
            );
            
            return {
                success: true,
                message: 'Password reset successfully',
                data: response.data
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Password reset failed';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    // ============================================================================
    // OTP AUTHENTICATION METHODS
    // ============================================================================

    /**
     * Initiate OTP-based login
     * @param {string} username - Username or email
     * @param {string} password - Password
     * @returns {Promise<Object>} OTP initiation response
     */
    async initiateOTPLogin(username, password) {
        try {
            this.log('Initiating OTP login', { username });
            
            const response = await axios.post(
                `${API_BASE_URL}/auth/otp/initiate-login`,
                {
                    username,
                    password
                },
                {
                    timeout: AUTH_CONFIG.REQUEST_TIMEOUT
                }
            );
            
            if (response.status === 200 && response.data.success) {
                this.log('OTP login initiated successfully', { 
                    sessionId: response.data.session_id,
                    emailMasked: response.data.email_masked
                });
                
                return {
                    success: true,
                    message: response.data.message,
                    data: response.data
                };
            } else {
                throw new Error(response.data.message || 'OTP initiation failed');
            }
        } catch (error) {
            this.log('OTP login initiation failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to initiate OTP login';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Verify OTP and complete login
     * @param {string} sessionId - OTP session ID
     * @param {string} otpCode - 6-digit OTP code
     * @returns {Promise<Object>} Verification response
     */
    async verifyOTP(sessionId, otpCode) {
        try {
            this.log('Verifying OTP', { sessionId });
            
            const response = await axios.post(
                `${API_BASE_URL}/auth/otp/verify`,
                {
                    session_id: sessionId,
                    otp_code: otpCode
                },
                {
                    timeout: AUTH_CONFIG.REQUEST_TIMEOUT
                }
            );
            
            if (response.status === 200 && response.data.success) {
                this.log('OTP verified successfully');
                
                // Store tokens and user info (same as regular login)
                this.setTokens(
                    response.data.access_token,
                    response.data.refresh_token
                );
                this.setUserInfo(response.data.user_info);
                
                // Schedule token refresh
                this.scheduleTokenRefresh(response.data.expires_in);
                
                return {
                    success: true,
                    message: response.data.message,
                    data: response.data.user_info,
                    access_token: response.data.access_token,
                    refresh_token: response.data.refresh_token,
                    session_info: response.data.session_info
                };
            } else {
                this.log('OTP verification failed', { reason: response.data.message });
                return {
                    success: false,
                    message: response.data.message,
                    remaining_attempts: response.data.remaining_attempts
                };
            }
        } catch (error) {
            this.log('OTP verification error', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'OTP verification failed';

            return {
                success: false,
                message: errorMessage,
                remaining_attempts: error.response?.data?.remaining_attempts,
                error: error.response?.data
            };
        }
    }

    /**
     * Resend OTP code
     * @param {string} sessionId - OTP session ID
     * @returns {Promise<Object>} Resend response
     */
    async resendOTP(sessionId) {
        try {
            this.log('Resending OTP', { sessionId });
            
            const response = await axios.post(
                `${API_BASE_URL}/auth/otp/resend`,
                {
                    session_id: sessionId
                },
                {
                    timeout: AUTH_CONFIG.REQUEST_TIMEOUT
                }
            );
            
            if (response.status === 200 && response.data.success) {
                this.log('OTP resent successfully');
                
                return {
                    success: true,
                    message: response.data.message,
                    data: response.data
                };
            } else {
                throw new Error(response.data.message || 'Failed to resend OTP');
            }
        } catch (error) {
            this.log('OTP resend failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to resend OTP';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    /**
     * Get OTP session status
     * @param {string} sessionId - OTP session ID
     * @returns {Promise<Object>} Status response
     */
    async getOTPStatus(sessionId) {
        try {
            this.log('Getting OTP status', { sessionId });
            
            const response = await axios.get(
                `${API_BASE_URL}/auth/otp/status/${sessionId}`,
                {
                    timeout: AUTH_CONFIG.REQUEST_TIMEOUT
                }
            );
            
            if (response.status === 200) {
                this.log('OTP status retrieved successfully', response.data);
                
                return {
                    success: true,
                    message: 'Status retrieved successfully',
                    data: response.data
                };
            } else {
                throw new Error('Failed to get OTP status');
            }
        } catch (error) {
            this.log('OTP status retrieval failed', {
                error: error.message,
                status: error.response?.status
            });

            const errorMessage = error.response?.data?.error ||
                               error.response?.data?.detail ||
                               error.response?.data?.message ||
                               error.message ||
                               'Failed to get OTP status';

            return {
                success: false,
                message: errorMessage,
                error: error.response?.data
            };
        }
    }

    // ============================================================================
    // END OTP AUTHENTICATION METHODS
    // ============================================================================

    /**
     * Logging utility
     * @param {string} message - Log message
     * @param {Object} data - Additional data
     */
    log(message, data = null) {
        if (DEBUG_MODE) {
            console.log(`[AuthService] ${message}`, data);
        }
    }
}

// Create singleton instance
const authService = new AuthService();

export default authService; 